import React, { useCallback, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  UserCheck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EMPTY_DATA_TRANSFER_FORM,
  EMPTY_COLUMN_MAPPING,
  DATA_TRANSFER_FIELDS,
  applyColumnMappingToRows,
  extractSheetHeaders,
  formatDataTransferDate,
  formatDataTransferMoney,
  formatImportPreviewValue,
  guessColumnMapping,
  isDataTransferRowEmpty,
} from "@/utils/crmDataTransferHelpers";

const CrmDataTransferPage = ({
  API,
  isDark,
  textClass,
  labelClass,
  inputClass,
  companies,
  isSuperAdmin,
  userCompanyId,
  visibleAgents,
}) => {
  const fileInputRef = useRef(null);
  const workbookRef = useRef(null);
  const [activePanel, setActivePanel] = useState("list");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [batches, setBatches] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCompanyId, setAssignCompanyId] = useState("");
  const [assignAgentId, setAssignAgentId] = useState("");
  const [assignTargetIds, setAssignTargetIds] = useState([]);
  const [formData, setFormData] = useState(EMPTY_DATA_TRANSFER_FORM);
  const [workbookMeta, setWorkbookMeta] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [sheetHeaders, setSheetHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState(EMPTY_COLUMN_MAPPING);
  const [importRows, setImportRows] = useState([]);
  const [importPreview, setImportPreview] = useState([]);
  const [importCompanyId, setImportCompanyId] = useState(isSuperAdmin ? "" : (userCompanyId ? String(userCompanyId) : ""));

  const filterAgentsByCompany = useCallback((agentList, companyId) => {
    if (!companyId || companyId === "all" || companyId === "none") return agentList;
    return agentList.filter((a) => String(a.company_id) === String(companyId));
  }, []);

  const listScopedAgents = useMemo(() => {
    if (isSuperAdmin) {
      if (companyFilter === "all") return [];
      return filterAgentsByCompany(visibleAgents, companyFilter);
    }
    return filterAgentsByCompany(visibleAgents, userCompanyId);
  }, [visibleAgents, isSuperAdmin, companyFilter, userCompanyId, filterAgentsByCompany]);

  const formScopedAgents = useMemo(
    () => filterAgentsByCompany(visibleAgents, isSuperAdmin ? importCompanyId : userCompanyId),
    [visibleAgents, isSuperAdmin, importCompanyId, userCompanyId, filterAgentsByCompany]
  );

  const assignScopedAgents = useMemo(
    () => filterAgentsByCompany(visibleAgents, isSuperAdmin ? assignCompanyId : userCompanyId),
    [visibleAgents, isSuperAdmin, assignCompanyId, userCompanyId, filterAgentsByCompany]
  );

  const handleCompanyFilterChange = (value) => {
    setCompanyFilter(value);
    setAgentFilter("all");
    setPage(1);
  };

  const handleFormCompanyChange = (value) => {
    const next = value === "none" ? "" : value;
    setImportCompanyId(next);
    setFormData((prev) => ({ ...prev, assigned_agent_id: "" }));
  };

  const handleAssignCompanyChange = (value) => {
    const next = value === "none" ? "" : value;
    setAssignCompanyId(next);
    setAssignAgentId("");
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));
  const shellClass = isDark
    ? "rounded-2xl border border-slate-800/80 bg-slate-950/50"
    : "rounded-2xl border border-gray-200 bg-white shadow-sm";

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        list: 1,
        page,
        limit: 20,
        search: searchTerm || undefined,
        agent_id: agentFilter !== "all" ? agentFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        batch_id: batchFilter !== "all" ? batchFilter : undefined,
        company_id: isSuperAdmin && companyFilter !== "all" ? companyFilter : undefined,
      };
      const res = await axios.get(`${API}/crm/data-transfer`, { params });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kayıtlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [API, page, searchTerm, agentFilter, statusFilter, batchFilter, companyFilter, isSuperAdmin]);

  const fetchBatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/data-transfer`, { params: { batches: 1 } });
      setBatches(res.data?.items || []);
    } catch {
      setBatches([]);
    }
  }, [API]);

  React.useEffect(() => {
    if (activePanel === "list") {
      fetchList();
      fetchBatches();
    }
  }, [activePanel, fetchList, fetchBatches]);

  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : items.map((item) => item.id));
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openAssignDialog = (ids) => {
    if (!ids.length) {
      toast.error("Lütfen en az bir kayıt seçin");
      return;
    }
    setAssignTargetIds(ids);
    setAssignCompanyId(isSuperAdmin ? (companyFilter !== "all" ? companyFilter : "") : (userCompanyId ? String(userCompanyId) : ""));
    setAssignAgentId("");
    setAssignOpen(true);
  };

  const handleAssign = async () => {
    if (isSuperAdmin && !assignCompanyId) {
      toast.error("Önce firma seçin");
      return;
    }
    if (!assignAgentId) {
      toast.error("Agent seçin");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/crm/data-transfer?assign=1`, {
        ids: assignTargetIds,
        agent_id: parseInt(assignAgentId, 10),
      });
      toast.success(`${assignTargetIds.length} kayıt agenta atandı`);
      setAssignOpen(false);
      setSelectedIds([]);
      fetchList();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Atama yapılamadı");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`${selectedIds.length} kaydı silmek istediğinize emin misiniz?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/crm/data-transfer`, { data: { ids: selectedIds } });
      toast.success("Kayıtlar silindi");
      setSelectedIds([]);
      fetchList();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silinemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.telefon.trim()) {
      toast.error("Telefon zorunludur");
      return;
    }
    if (isSuperAdmin && !importCompanyId) {
      toast.error("Önce firma seçin");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        yas: formData.yas !== "" ? formData.yas : null,
        boy: formData.boy !== "" ? formData.boy : null,
        kilo: formData.kilo !== "" ? formData.kilo : null,
        fiyat: formData.fiyat !== "" ? formData.fiyat : null,
        assigned_agent_id: formData.assigned_agent_id ? parseInt(formData.assigned_agent_id, 10) : null,
        company_id: isSuperAdmin && importCompanyId ? parseInt(importCompanyId, 10) : undefined,
      };
      await axios.post(`${API}/crm/data-transfer`, payload);
      toast.success("Kayıt eklendi");
      setFormData(EMPTY_DATA_TRANSFER_FORM);
      setActivePanel("list");
      fetchList();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kayıt eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  const recomputeMappedRows = useCallback((rows, mapping) => {
    const mapped = applyColumnMappingToRows(rows, mapping);
    setImportRows(mapped);
    setImportPreview(mapped.slice(0, 200));
    return mapped;
  }, []);

  const loadSheetRows = useCallback((workbook, sheetName) => {
    if (!workbook || !sheetName || !workbook.Sheets[sheetName]) {
      setRawRows([]);
      setSheetHeaders([]);
      setColumnMapping(EMPTY_COLUMN_MAPPING);
      setImportRows([]);
      setImportPreview([]);
      return;
    }
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "", raw: false });
    const headers = extractSheetHeaders(rows);
    const guessed = guessColumnMapping(headers);
    setRawRows(rows);
    setSheetHeaders(headers);
    setColumnMapping(guessed);
    recomputeMappedRows(rows, guessed);
  }, [recomputeMappedRows]);

  const handleMappingChange = (fieldKey, headerValue) => {
    const next = { ...columnMapping, [fieldKey]: headerValue === "__skip__" ? "" : headerValue };
    setColumnMapping(next);
    recomputeMappedRows(rawRows, next);
  };

  const resetImportState = () => {
    workbookRef.current = null;
    setWorkbookMeta(null);
    setSelectedSheet("");
    setRawRows([]);
    setSheetHeaders([]);
    setColumnMapping(EMPTY_COLUMN_MAPPING);
    setImportRows([]);
    setImportPreview([]);
  };

  const handleExcelFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
      workbookRef.current = workbook;
      setWorkbookMeta({ name: file.name, sheets: workbook.SheetNames });
      const firstSheet = workbook.SheetNames[0] || "";
      setSelectedSheet(firstSheet);
      loadSheetRows(workbook, firstSheet);
      if (firstSheet) {
        toast.success(`Excel yüklendi — kolon eşleştirmesini kontrol edin (${firstSheet})`);
      }
    } catch {
      toast.error("Excel dosyası okunamadı");
      resetImportState();
    }
  };

  const reloadSheetPreview = useCallback((sheetName) => {
    setSelectedSheet(sheetName);
    loadSheetRows(workbookRef.current, sheetName);
  }, [loadSheetRows]);

  const handleImport = async () => {
    if (!columnMapping.telefon) {
      toast.error("Telefon kolonu eşleştirmesi zorunludur");
      return;
    }
    if (!importRows.length) {
      toast.error("İçe aktarılacak satır yok — kolon eşleştirmesini kontrol edin");
      return;
    }
    if (isSuperAdmin && !importCompanyId) {
      toast.error("Önce firma seçin");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/crm/data-transfer?import=1`, {
        rows: importRows,
        sheet_name: selectedSheet || workbookMeta?.name || null,
        company_id: isSuperAdmin && importCompanyId ? parseInt(importCompanyId, 10) : undefined,
      });
      toast.success(`${res.data?.inserted || 0} kayıt içe aktarıldı${res.data?.skipped ? `, ${res.data.skipped} atlandı` : ""}`);
      resetImportState();
      setActivePanel("list");
      fetchList();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İçe aktarma başarısız");
    } finally {
      setSaving(false);
    }
  };

  const panelTabs = useMemo(() => ([
    { id: "list", label: "Kayıt Listesi", icon: Users },
    { id: "form", label: "Yeni Kayıt", icon: Plus },
    { id: "import", label: "Excel İçe Aktar", icon: Upload },
  ]), []);

  return (
    <div className="space-y-4">
      <div className={`${shellClass} p-5 sm:p-6`}>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDark ? "bg-cyan-500/20" : "bg-cyan-100"}`}>
                <FileSpreadsheet className={`w-4 h-4 ${isDark ? "text-cyan-400" : "text-cyan-700"}`} />
              </div>
              <h2 className={`text-lg font-semibold ${textClass}`}>Data Aktarımı</h2>
            </div>
            <p className={`text-sm ${labelClass}`}>Excel veya form ile lead kaydı — sonrasında agenta tek tek veya toplu atama</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {panelTabs.map(({ id, label, icon: Icon }) => (
              <Button
                key={id}
                type="button"
                size="sm"
                variant={activePanel === id ? "default" : "outline"}
                onClick={() => setActivePanel(id)}
                className={activePanel !== id && isDark ? "border-slate-700" : ""}
              >
                <Icon className="w-4 h-4 mr-1.5" />
                {label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {activePanel === "list" && (
        <div className={`${shellClass} p-4 sm:p-5 space-y-4`}>
          <div className="flex flex-col xl:flex-row gap-3 xl:items-center xl:justify-between">
            <div className="relative flex-1 max-w-lg">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${labelClass}`} />
              <Input
                placeholder="Telefon, isim, hastalık, görüşme sonucu..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setSearchTerm(searchInput.trim()); setPage(1); } }}
                className={`pl-10 ${inputClass}`}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => { setSearchTerm(searchInput.trim()); setPage(1); }}>Ara</Button>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className={`w-36 ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tüm durumlar</SelectItem>
                  <SelectItem value="pending">Atama bekliyor</SelectItem>
                  <SelectItem value="assigned">Atandı</SelectItem>
                </SelectContent>
              </Select>
              {isSuperAdmin && (
                <Select value={companyFilter} onValueChange={handleCompanyFilterChange}>
                  <SelectTrigger className={`w-40 ${inputClass}`}><SelectValue placeholder="Firma" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="all">Tüm firmalar</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select
                value={agentFilter}
                onValueChange={(v) => { setAgentFilter(v); setPage(1); }}
                disabled={isSuperAdmin && companyFilter === "all"}
              >
                <SelectTrigger className={`w-40 ${inputClass}`}>
                  <SelectValue placeholder={isSuperAdmin && companyFilter === "all" ? "Önce firma seçin" : "Agent"} />
                </SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tüm agentlar</SelectItem>
                  <SelectItem value="unassigned">Atanmamış</SelectItem>
                  {listScopedAgents.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.ad_soyad}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={batchFilter} onValueChange={(v) => { setBatchFilter(v); setPage(1); }}>
                <SelectTrigger className={`w-44 ${inputClass}`}><SelectValue placeholder="Import batch" /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tüm batchler</SelectItem>
                  {batches.map((b) => (
                    <SelectItem key={b.import_batch_id} value={b.import_batch_id}>
                      {(b.import_sheet_name || "Sheet")} ({b.row_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={fetchList} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div className={`flex flex-wrap items-center gap-2 rounded-xl px-3 py-2 ${isDark ? "bg-cyan-950/40 border border-cyan-900/50" : "bg-cyan-50 border border-cyan-100"}`}>
              <span className={`text-sm ${isDark ? "text-cyan-200" : "text-cyan-800"}`}>{selectedIds.length} kayıt seçildi</span>
              <Button size="sm" onClick={() => openAssignDialog(selectedIds)} disabled={saving}>
                <UserCheck className="w-4 h-4 mr-1" /> Toplu Ata
              </Button>
              <Button size="sm" variant="destructive" onClick={handleDeleteSelected} disabled={saving}>
                <Trash2 className="w-4 h-4 mr-1" /> Sil
              </Button>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-inherit">
            <table className="min-w-full text-sm">
              <thead className={isDark ? "bg-slate-900/80 text-slate-300" : "bg-gray-50 text-gray-600"}>
                <tr>
                  <th className="px-3 py-2 w-10">
                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} />
                  </th>
                  <th className="px-3 py-2 text-left">Tarih</th>
                  <th className="px-3 py-2 text-left">Telefon</th>
                  <th className="px-3 py-2 text-left">İsim</th>
                  <th className="px-3 py-2 text-left">Hastalık</th>
                  <th className="px-3 py-2 text-left">Görüşme</th>
                  <th className="px-3 py-2 text-left">Fiyat</th>
                  <th className="px-3 py-2 text-left">Agent</th>
                  <th className="px-3 py-2 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="px-3 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={9} className={`px-3 py-10 text-center ${labelClass}`}>Kayıt bulunamadı</td></tr>
                ) : items.map((item) => (
                  <tr key={item.id} className={isDark ? "border-t border-slate-800" : "border-t border-gray-100"}>
                    <td className="px-3 py-2">
                      <Checkbox checked={selectedIds.includes(item.id)} onCheckedChange={() => toggleSelectOne(item.id)} />
                    </td>
                    <td className={`px-3 py-2 whitespace-nowrap ${textClass}`}>{formatDataTransferDate(item.tarih)}</td>
                    <td className={`px-3 py-2 whitespace-nowrap ${textClass}`}>{item.telefon || "—"}</td>
                    <td className={`px-3 py-2 ${textClass}`}>{item.ad_soyad || "—"}</td>
                    <td className={`px-3 py-2 ${textClass}`}>{item.hastalik || "—"}</td>
                    <td className="px-3 py-2">
                      {item.gorusme_sonucu ? (
                        <Badge variant="secondary" className={isDark ? "bg-slate-800" : ""}>{item.gorusme_sonucu}</Badge>
                      ) : "—"}
                    </td>
                    <td className={`px-3 py-2 whitespace-nowrap ${textClass}`}>{formatDataTransferMoney(item.fiyat)}</td>
                    <td className={`px-3 py-2 ${textClass}`}>
                      {item.assigned_agent_name ? (
                        <span className={isDark ? "text-emerald-300" : "text-emerald-700"}>{item.assigned_agent_name}</span>
                      ) : (
                        <span className={labelClass}>Atanmadı</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => openAssignDialog([item.id])} className={isDark ? "border-slate-700" : ""}>
                        Ata
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className={`text-sm ${labelClass}`}>{total.toLocaleString("tr-TR")} kayıt</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className={`text-sm ${labelClass}`}>{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {activePanel === "form" && (
        <form onSubmit={handleFormSubmit} className={`${shellClass} p-5 sm:p-6 space-y-4`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ["tarih", "Tarih", "date"],
              ["telefon", "Telefon *", "text"],
              ["ad_soyad", "İsim/Soyisim", "text"],
              ["yas", "Yaş", "number"],
              ["boy", "Boy (m)", "number"],
              ["kilo", "Kilo (kg)", "number"],
              ["belge", "Belge", "text"],
              ["hastalik", "Hastalık", "text"],
              ["gorusme_sonucu", "Görüşme Sonucu", "text"],
              ["fiyat", "Fiyat", "number"],
            ].map(([key, label, type]) => (
              <div key={key}>
                <Label className={labelClass}>{label}</Label>
                <Input
                  type={type}
                  step={type === "number" ? "any" : undefined}
                  value={formData[key]}
                  onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                  className={`mt-1.5 ${inputClass}`}
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className={labelClass}>Notlar</Label>
              <Input
                value={formData.notlar}
                onChange={(e) => setFormData({ ...formData, notlar: e.target.value })}
                className={`mt-1.5 ${inputClass}`}
              />
            </div>
            {isSuperAdmin && (
              <div>
                <Label className={labelClass}>Firma *</Label>
                <Select value={importCompanyId || "none"} onValueChange={handleFormCompanyChange}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue placeholder="Firma seç" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="none">Firma seçin</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className={labelClass}>Agent (opsiyonel)</Label>
              <Select
                value={formData.assigned_agent_id || "none"}
                onValueChange={(v) => setFormData({ ...formData, assigned_agent_id: v === "none" ? "" : v })}
                disabled={isSuperAdmin && !importCompanyId}
              >
                <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue placeholder={isSuperAdmin && !importCompanyId ? "Önce firma seçin" : "Agent seç"} /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="none">Atama yok</SelectItem>
                  {formScopedAgents.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.ad_soyad}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Kaydet
            </Button>
          </div>
        </form>
      )}

      {activePanel === "import" && (
        <div className={`${shellClass} p-5 sm:p-6 space-y-4`}>
          <div className={`rounded-xl border border-dashed p-6 text-center ${isDark ? "border-slate-700 bg-slate-900/40" : "border-gray-300 bg-gray-50"}`}>
            <Upload className={`w-10 h-10 mx-auto mb-3 ${labelClass}`} />
            <p className={`text-sm mb-4 ${labelClass}`}>
              Excel dosyanızı yükleyin, sheet ve kolon eşleştirmesini yapın, ardından içe aktarın.
            </p>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleExcelFile} />
            <Button type="button" onClick={() => fileInputRef.current?.click()}>
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel Seç
            </Button>
            {workbookMeta && (
              <p className={`text-xs mt-3 ${labelClass}`}>{workbookMeta.name} — {workbookMeta.sheets.length} sheet</p>
            )}
          </div>

          {workbookMeta && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {isSuperAdmin && (
                <div>
                  <Label className={labelClass}>Firma *</Label>
                  <Select value={importCompanyId || "none"} onValueChange={handleFormCompanyChange}>
                    <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue placeholder="Firma seç" /></SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                      <SelectItem value="none">Firma seçin</SelectItem>
                      {companies.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className={labelClass}>Sheet seçin</Label>
                <Select value={selectedSheet} onValueChange={reloadSheetPreview}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue placeholder="Sheet" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    {workbookMeta.sheets.map((sheet) => <SelectItem key={sheet} value={sheet}>{sheet}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className={`text-xs mt-2 ${labelClass}`}>{rawRows.length.toLocaleString("tr-TR")} satır okundu</p>
              </div>
            </div>
          )}

          {sheetHeaders.length > 0 && (
            <div className={`rounded-xl border p-4 space-y-3 ${isDark ? "border-slate-800 bg-slate-900/30" : "border-gray-200 bg-gray-50/80"}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className={`text-sm font-medium ${textClass}`}>Kolon Eşleştirme</p>
                  <p className={`text-xs ${labelClass}`}>Her alan için Excel kolonunu seçin. Telefon zorunludur.</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={isDark ? "border-slate-700" : ""}
                  onClick={() => {
                    const guessed = guessColumnMapping(sheetHeaders);
                    setColumnMapping(guessed);
                    recomputeMappedRows(rawRows, guessed);
                    toast.success("Kolonlar otomatik eşleştirildi");
                  }}
                >
                  Otomatik Eşleştir
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {DATA_TRANSFER_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <Label className={`text-xs ${labelClass}`}>
                      {label}{key === "telefon" ? " *" : ""}
                    </Label>
                    <Select
                      value={columnMapping[key] || "__skip__"}
                      onValueChange={(v) => handleMappingChange(key, v)}
                    >
                      <SelectTrigger className={`mt-1 h-9 text-xs ${inputClass}`}>
                        <SelectValue placeholder="Kolon seç" />
                      </SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <SelectItem value="__skip__">— Eşleştirme —</SelectItem>
                        {sheetHeaders.map((header) => (
                          <SelectItem key={`${key}-${header}`} value={header}>{header}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {importPreview.length > 0 && (
            <>
              <div className="flex items-center justify-between gap-3">
                <Badge variant="secondary">{importRows.length} satır içe aktarılacak</Badge>
                <Button onClick={handleImport} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  İçe Aktar
                </Button>
              </div>
              <div className="overflow-x-auto rounded-xl border border-inherit max-h-[420px]">
                <table className="min-w-full text-xs">
                  <thead className={isDark ? "bg-slate-900/80 sticky top-0" : "bg-gray-50 sticky top-0"}>
                    <tr>
                      <th className="px-2 py-2 text-left">Tarih</th>
                      <th className="px-2 py-2 text-left">Telefon</th>
                      <th className="px-2 py-2 text-left">İsim</th>
                      <th className="px-2 py-2 text-left">Hastalık</th>
                      <th className="px-2 py-2 text-left">Görüşme</th>
                      <th className="px-2 py-2 text-left">Fiyat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.slice(0, 50).map((row, idx) => (
                      <tr key={idx} className={isDark ? "border-t border-slate-800" : "border-t border-gray-100"}>
                        <td className="px-2 py-1.5">{formatImportPreviewValue(row.tarih, "tarih")}</td>
                        <td className="px-2 py-1.5">{formatImportPreviewValue(row.telefon, "telefon")}</td>
                        <td className="px-2 py-1.5">{formatImportPreviewValue(row.ad_soyad, "ad_soyad")}</td>
                        <td className="px-2 py-1.5">{formatImportPreviewValue(row.hastalik, "hastalik")}</td>
                        <td className="px-2 py-1.5">{formatImportPreviewValue(row.gorusme_sonucu, "gorusme_sonucu")}</td>
                        <td className="px-2 py-1.5">{formatImportPreviewValue(row.fiyat, "fiyat")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className={isDark ? "bg-slate-900 border-slate-800 text-white" : ""}>
          <DialogHeader>
            <DialogTitle>Agent Atama</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {isSuperAdmin && (
              <div>
                <Label className={labelClass}>Firma *</Label>
                <Select value={assignCompanyId || "none"} onValueChange={handleAssignCompanyChange}>
                  <SelectTrigger className={`mt-2 ${inputClass}`}><SelectValue placeholder="Firma seç" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="none">Firma seçin</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className={labelClass}>{assignTargetIds.length} kayıt için agent seçin</Label>
              <Select
                value={assignAgentId}
                onValueChange={setAssignAgentId}
                disabled={isSuperAdmin && !assignCompanyId}
              >
                <SelectTrigger className={`mt-2 ${inputClass}`}>
                  <SelectValue placeholder={isSuperAdmin && !assignCompanyId ? "Önce firma seçin" : "Agent seç"} />
                </SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  {assignScopedAgents.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.ad_soyad}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>İptal</Button>
            <Button onClick={handleAssign} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Ata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmDataTransferPage;
